import React, {ChangeEvent, useState} from "react";
import {
  IColumn,
  IContentAreaProps,
  IShiftHistory,
  IState,
  Order
} from "../../../../types";
import {useSelector} from "react-redux";
import {useFetch} from "../../../../system/useFetch";
import FilterComponentBase from "../../../../components/filterComponentBase";
import SkeletonView from "../../../../components/skeletonView";
import {
  Alert,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip
} from "@mui/material";
import TableContainer from "../../../../components/tableContainer";
import TablePaginationActions from "../../../../components/tablePaginationActions";
import {formatDate, stableSort, toTons} from "../../../../system/utils";
import IconButton from "@mui/material/IconButton";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import StyledTableRow from "../../../../components/styledTableRow";

const pollInterval = 300000;  // 5 min

export interface IMillYield {
  shiftYieldID: number;
  product: string | null;
  heat: string;
  steel: {
    steelID: number;
    code: string;
    family: string | null
  } | null,
  workOrder: string;
  billetCount: number;
  totalWeight: number;
  ccShear: number;
  cdShear: number;
  hss: number;
  billetLength: number;
  shiftHistory: IShiftHistory;
}

const columns: IColumn[] = [
  {
    id: "factoryDay",
    label: "Factory Day",
    minWidth: 0,
    accessor: (row: IMillYield) => formatDate(row.shiftHistory.factoryDay)
  },
  {
    id: "shift",
    label: "Shift",
    minWidth: 0,
    accessor: (row: IMillYield) => row.shiftHistory.name
  },
  {
    id: "workOrder",
    label: "Work Order",
    minWidth: 0
  },
  {
    id: "heat",
    label: "Heat Number",
    minWidth: 0
  },
  {
    id: "steel",
    label: "Steel Grade",
    minWidth: 0,
    accessor: (row: IMillYield) => row.steel ? row.steel.code : ""
  },
  {
    id: "product",
    label: "Product",
    minWidth: 0,
    align: "center"
  },
  {
    id: "billetCount",
    label: "Billet Count",
    minWidth: 0,
    align: "right"
  },
  {
    id: "totalWeight",
    label: "Weight",
    subLabel: "US Tons",
    minWidth: 0,
    align: "right",
    accessor: (row: IMillYield) => toTons(row.totalWeight).toFixed(2)
  },
  {
    id: "ccShear",
    label: "C&C Shear",
    subLabel: "US Tons",
    minWidth: 0,
    align: "right",
    render: value => value.toFixed(2)
  },
  {
    id: "cdShear",
    label: "C&D Shear",
    subLabel: "US Tons",
    minWidth: 0,
    align: "right",
    render: value => value.toFixed(2)
  },
  {
    id: "hss",
    label: "HSS",
    subLabel: "US Tons",
    minWidth: 0,
    align: "right",
    render: value => value.toFixed(2)
  },
  // {
  //   id: "billetLength",
  //   label: "billetLength",
  //   minWidth: 0,
  //   align: "right",
  //   render: value => value.toFixed(2)
  // }
];



function descendingComparator(a: IMillYield, b: IMillYield, orderBy: keyof IMillYield | "shift") {

  if (orderBy === "shiftHistory") {
    if (b.shiftHistory.factoryDay < a.shiftHistory.factoryDay) {
      return -1;
    }

    return b.shiftHistory.factoryDay > a.shiftHistory.factoryDay ? 1 : 0;
  }

  if (orderBy === "shift") {
    if (b.shiftHistory.name < a.shiftHistory.name) {
      return -1;
    }

    return b.shiftHistory.name > a.shiftHistory.name ? 1 : 0;
  }

  if (orderBy === "workOrder") {
    if (b.workOrder < a.workOrder) {
      return -1;
    }

    return b.workOrder > a.workOrder ? 1 : 0;
  }

  if (orderBy === "heat") {
    if (b.heat < a.heat) {
      return -1;
    }

    return b.heat > a.heat ? 1 : 0;
  }


  if (orderBy === "steel") {
    if ((b.steel?.code ?? "") < (a.steel?.code ?? "")) {
      return -1;
    }

    return (b.steel?.code ?? "") > (a.steel?.code ?? "") ? 1 : 0;
  }


  if (orderBy === "billetCount") {
    if (b.billetCount < a.billetCount) {
      return -1;
    }

    return b.billetCount > a.billetCount ? 1 : 0;
  }

  if (orderBy === "totalWeight") {
    if (b.totalWeight < b.totalWeight) {
      return -1;
    }

    return b.totalWeight > b.totalWeight ? 1 : 0;
  }


  return 0;
}

function getComparator(
    order: Order,
    orderBy: keyof IMillYield | "shift"
): (a: IMillYield, b: IMillYield) => number {
  return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
}


const YieldReport = ({ parentSize }: IContentAreaProps) => {
  const filter = useSelector((state: IState) => state.filter);
  const { loading, error, data: yieldReportData } = useFetch<IMillYield[]>(
      {
        module: "mill",
        query: "getMillYieldReportByFilter",
        payload: { ...filter.remote }
      }, filter.local.mode === "REAL_TIME" ? pollInterval : 0);
  const [order, setOrder] = useState<Order>("desc");
  const [orderBy, setOrderBy] = useState<keyof IMillYield | "shift">("steel");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const dataLength = yieldReportData ? yieldReportData.length : 0;
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - dataLength) : 0;
  const tableContainerHeight = parentSize.height - 50;

  const gridStyle = {
    ...parentSize,
    gridTemplateRows: "auto 1fr",
    gridTemplateColumns: "1fr"
  };

  const hasData = yieldReportData && yieldReportData.length > 0;


  const createSortHandler = (property: keyof IMillYield | "shift") => () => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number,) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };


  return (
    <div className="display-grid" style={gridStyle}>
      <FilterComponentBase loading={loading} disabled={{ shift: true, crew: true }}/>

      {loading && <SkeletonView parentSize={parentSize}/>}
      {error && <Alert severity="warning">{error.message}</Alert>}
      {!loading && !error && !hasData && <Alert severity="warning">No Data to Display.</Alert>}


      {hasData && !loading && !error &&
        <>
          <TableContainer style={{ height: tableContainerHeight, maxHeight: tableContainerHeight }}>
            <Table stickyHeader={true} size={"small"}>
              <TableHead>
                <TableRow>
                  {columns.map((column) =>
                      column.id !== ""
                          ? <TableCell
                              className="table-header"
                              key={column.id}
                              align={column.align}
                              sortDirection={orderBy === column.id ? order : false}>
                            <TableSortLabel
                                active={orderBy === column.id}
                                direction={orderBy === column.id ? order : "asc"}
                                onClick={createSortHandler(column.id as keyof IMillYield | "shift")}>
                              <span>{column.label}{column.subLabel ? <sub className="ml-1 mr-1">{column.subLabel}</sub> : ""}</span>

                              {orderBy === column.id
                                ? <span className="cell-visuallyHidden">{order === "desc" ? "sorted descending" : "sorted ascending"}</span>
                                : null
                              }
                            </TableSortLabel>
                          </TableCell>
                          : <TableCell className="table-header" style={{ width: 20 }}/>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                { yieldReportData && stableSort(yieldReportData, getComparator(order, orderBy))
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, index) => {

                      return (
                          <StyledTableRow hover={true} role="checkbox" tabIndex={-1} key={`yield-row-${index}`}>
                            {columns.map((column: IColumn) => {
                              const value = column.accessor ? column.accessor(row) : (row as any)[column.id];

                              if (column.render) {
                                return (
                                    <TableCell key={`${column.id}-${index}`} align={column.align}>
                                      {column.render(value)}
                                    </TableCell>
                                );
                              }

                              return (
                                  <TableCell key={`${column.id}-${index}`} align={column.align}>
                                    {column.format ? column.format(value) : value}
                                  </TableCell>
                              );
                            })}
                          </StyledTableRow>
                      );
                    })}
                {emptyRows > 0 &&
                    <TableRow style={{ height: 53 * emptyRows }}>
                      <TableCell colSpan={6}/>
                    </TableRow>
                }
              </TableBody>
            </Table>
          </TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50, { label: "All", value: -1 }]}
                    colSpan={columns.length + 1}
                    count={dataLength}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableHead>
          </Table>
        </>
      }

    </div>
  );
};

export default YieldReport;